import { useMemo } from 'react';
import { DemoChip, getHourlyInsightsLabel, InfoTipPopover } from '@frontend/analytics-shared';
import { BarChartAppearance, BarChartData, Chart } from '@frontend/charts';
import { i18next, useTranslation } from '@frontend/i18n';
import { formatters } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { useFetchAggregatedCallAnswerRate } from '../../hooks/use-fetch-aggregated-call-answer-rate';
import { usePhoneAnalyticsShallowStore } from '../../hooks/use-phone-analytics-store';
import { ChartProps } from './shared-types';

const colors = {
  incoming: theme.colors.secondary.seaweed30,
  weaveAverage: theme.colors.primary20,
};

const labels = {
  incoming: i18next.t('Incoming', { ns: 'analytics-phone' }),
  weaveAverage: i18next.t('Weave Average', { ns: 'analytics-phone' }),
};

export const CallAnswerRateChart = ({ defaultChartAppearance = {}, exportPdfProps, isSmallMode }: ChartProps) => {
  const { t } = useTranslation('analytics-phone');
  const { accessibleLocationsNames, filterHintText, isDemoAccount } = usePhoneAnalyticsShallowStore(
    'accessibleLocationsNames',
    'filterHintText',
    'isDemoAccount'
  );
  const { data, isHourlyData, isLoading, isMultiLocation } = useFetchAggregatedCallAnswerRate();

  const chartData: BarChartData = useMemo(() => {
    let groups: BarChartData['groups'] = [];

    if (isMultiLocation) {
      // There will be multiple location objects with single/multiple dates
      groups = Object.entries(data?.multiLocationsDailyRate || {}).map(([key, value]) => ({
        name: accessibleLocationsNames?.[key] || key,
        values: {
          incoming: value,
        },
      }));
    } else {
      // For hourly data: There will be only one object with single date and hourly data for the given location
      // For daily data: There will be only one object with multiple dates for the given location
      const locationData = Object.values(data?.locations || {})[0] || {};

      if (isHourlyData) {
        // Hourly data is restricted to a single date, hence only one object
        const { hourly = [] } = Object.values(locationData || {})[0] || {};
        groups = hourly.map((value, i) => ({
          name: getHourlyInsightsLabel(i),
          values: {
            incoming: value,
            // We do not have hourly weave averages, so no need to show it
          },
        }));
      } else {
        groups = Object.entries(locationData).map(([date, { daily }], index) => ({
          name: formatters.date(date),
          values: {
            incoming: daily,
            // The weave averages array have everyday's average for the given location in the same order as the dates
            weaveAverage: data?.weaveAverage?.[index] || 0,
          },
        }));
      }
    }

    return {
      areaKeys: isMultiLocation || isHourlyData ? [] : ['weaveAverage'],
      groups,
    };
  }, [accessibleLocationsNames, data, isHourlyData, isMultiLocation]);

  const appearance: Partial<BarChartAppearance> = {
    ...defaultChartAppearance,
    customYAxisTick: undefined,
    customYAxisTickFormat: formatters.appendPercent,
    groupsGap: 100,
  };

  return (
    <Chart colors={colors} isLoading={isLoading} labels={labels}>
      <Chart.Header
        {...exportPdfProps}
        infoTip={<InfoTipPopover>{t('Percentage of total incoming calls answered by the location')}</InfoTipPopover>}
        leftElement={isDemoAccount && <DemoChip />}
        subtitle={!isSmallMode && filterHintText}
        title={t('Call Answer Rate')}
      />
      <Chart.BarChart appearance={appearance} data={chartData} formatValue={formatters.appendPercent} />
      <Chart.Legends />
    </Chart>
  );
};
